import { useCallback, useEffect, useRef, useState } from 'react';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import { CUSTOM_EASE, DEFAULT_CURVE, formatCubicEase, isCubicEase } from '../../features/animations/ease';
import '../../features/animations/prefabs';
import { heldValue, playClip } from '../../features/animations/player';
import { propertiesForActor, resolveProperty } from '../../features/animations/registry';
import {
    CLIP_NAME,
    clearDrafts,
    deleteDraft,
    getPrefab,
    listClipEntries,
    listParts,
    listPrefabs,
    resolveClip,
    saveDraft,
} from '../../features/animations/prefab';
import {
    addAnimatable,
    addCustomProperty,
    addKey,
    addTrack,
    clearActorInitial,
    createEmptyClip,
    saveClip,
    easeOptions,
    normalizeClip,
    removeActor,
    removeCustomProperty,
    removeKey,
    removeTrack,
    setActorInitial,
    setClipFrameRate,
    setClipLength,
    snapTime,
    updateActor,
    updateKey,
} from '../../features/animations/clip';

import AnimationPreviewStage from './previewStage';
import CurveEditor from './CurveEditor';
import './style.css';

const rulerTicks = (length) => {
    const step = length <= 1 ? 0.1 : length <= 2 ? 0.2 : length <= 5 ? 0.5 : 1;
    const count = Math.round(length / step);
    const ticks = [];
    for (let index = 0; index <= count; index += 1) {
        ticks.push(Math.min(length, Math.round(index * step * 1000) / 1000));
    }
    return ticks;
};

const formatTime = (time) => `${time.toFixed(2)}s`;

const serializedClip = (clip) => JSON.stringify(normalizeClip(clip));

export default function AnimationEditorPage() {
    const { app, setAutoResize } = usePixiApp();
    const pixiRef = useRef(null);
    const stageRef = useRef(null);
    const lanesRef = useRef(null);
    const playbackRef = useRef(null);
    const playingRef = useRef(false);
    const clipRef = useRef(null);
    const clipNameRef = useRef('flourish');
    const prefabIdRef = useRef('packOpen');
    const loadedKey = useRef('');
    const releasePoseRef = useRef(false);
    const loadToken = useRef(0);
    const timeRef = useRef(0);
    const snapRef = useRef(true);
    const playRef = useRef(() => {});
    const pauseRef = useRef(() => {});

    const [prefabId, setPrefabId] = useState('packOpen');
    const [clipName, setClipName] = useState('flourish');
    const [clip, setClip] = useState(null);
    const [draftName, setDraftName] = useState('');
    const [, setLibraryRevision] = useState(0);
    const [time, setTime] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [snap, setSnap] = useState(true);
    const [ready, setReady] = useState(false);
    const [selection, setSelection] = useState(null);
    const [status, setStatus] = useState('Position and rotation are added to the current value. Scale multiplies it. Alpha and Visible are set on the part.');
    const [trackProperty, setTrackProperty] = useState('rotationY');
    const [customDraft, setCustomDraft] = useState({ id: '', label: '', defaultValue: 0, step: 0.01 });
    const [initialDraft, setInitialDraft] = useState({ property: 'alpha', value: 0 });

    const prefab = getPrefab(prefabId);
    const clipEntries = listClipEntries(prefab);
    const currentEntry = clipEntries.find((entry) => entry.name === clipName) || null;

    clipRef.current = clip;
    clipNameRef.current = clipName;
    prefabIdRef.current = prefabId;
    timeRef.current = time;
    snapRef.current = snap;

    const setPlayhead = (next) => {
        releasePoseRef.current = false;
        timeRef.current = next;
        setTime(next);
    };

    const halt = () => {
        playbackRef.current?.kill();
        playbackRef.current = null;
        playingRef.current = false;
        setPlaying(false);
    };

    const flushDraft = () => {
        const current = clipRef.current;
        const name = clipNameRef.current;
        const id = prefabIdRef.current;
        if (!current || !name || !id) return;
        const serialized = serializedClip(current);
        if (serialized === loadedKey.current) return;
        saveDraft(id, name, current);
        loadedKey.current = serialized;
        setLibraryRevision((value) => value + 1);
    };

    const pause = () => {
        releasePoseRef.current = false;
        halt();
        stageRef.current?.show(clipRef.current, timeRef.current);
    };

    const stop = () => {
        releasePoseRef.current = false;
        halt();
        timeRef.current = 0;
        setTime(0);
        stageRef.current?.show(clipRef.current, 0);
    };

    const play = () => {
        const stage = stageRef.current;
        const current = clipRef.current;
        if (!stage || !current) return;

        halt();
        releasePoseRef.current = false;
        const start = timeRef.current >= current.length - 0.001 ? 0 : timeRef.current;
        playingRef.current = true;
        setPlaying(true);

        const timeline = playClip(current, stage.prepare(), {
            onUpdate: (nextTime) => {
                timeRef.current = nextTime;
                setTime(nextTime);
            },
        });
        timeline.pause();
        playbackRef.current = timeline;
        const finished = timeline.eventCallback('onComplete');
        timeline.eventCallback('onComplete', () => {
            if (typeof finished === 'function') finished();
            playbackRef.current = null;
            playingRef.current = false;
            setPlaying(false);
            if (current.keepFinal !== true) {
                releasePoseRef.current = true;
                stage.resetTransforms();
                return;
            }
            timeRef.current = current.length;
            setTime(current.length);
        });
        timeline.seek(start, false);
        timeline.play();
    };

    playRef.current = play;
    pauseRef.current = pause;

    const timeFromClientX = (clientX) => {
        const rect = lanesRef.current?.getBoundingClientRect();
        const ratio = rect?.width ? (clientX - rect.left) / rect.width : 0;
        const current = clipRef.current;
        if (!current) return 0;
        return snapTime(ratio * current.length, current.frameRate, current.length, snapRef.current);
    };

    const openClip = async (nextPrefab, name) => {
        if (!nextPrefab || !name) return;
        if (nextPrefab.id === prefabIdRef.current && name === clipNameRef.current) return;
        try {
            flushDraft();
        } catch (error) {
            setStatus('Draft is too large for browser storage. Export the JSON to keep it.');
            return;
        }

        const token = loadToken.current + 1;
        loadToken.current = token;
        halt();

        try {
            await stageRef.current?.mountPrefab(nextPrefab, name);
        } catch (error) {
            setStatus('Could not show that prefab.');
            return;
        }
        if (token !== loadToken.current) return;

        const next = await resolveClip(nextPrefab, name);
        if (token !== loadToken.current) return;

        loadedKey.current = serializedClip(next);
        setPrefabId(nextPrefab.id);
        setClipName(name);
        setClip(next);
        setPlayhead(0);
        setSelection({ actorId: next.actors[0]?.id || null });
    };

    useEffect(() => {
        let cancelled = false;

        const boot = async () => {
            if (!app || !pixiRef.current) return;
            setAutoResize(true);
            const stage = new AnimationPreviewStage();
            try {
                await stage.attach(app);
            } catch (error) {
                setStatus('Could not start the preview.');
                return;
            }
            if (cancelled) {
                stage.detach(app);
                return;
            }
            if (!pixiRef.current.contains(app.canvas)) {
                pixiRef.current.appendChild(app.canvas);
            }
            stageRef.current = stage;

            const initialPrefab = getPrefab('packOpen') || listPrefabs()[0];
            if (!initialPrefab) {
                setReady(true);
                return;
            }
            try {
                const initialName = initialPrefab.clips.find((item) => item.name === 'flourish')?.name
                    || initialPrefab.clips[0]?.name
                    || null;
                await stage.mountPrefab(initialPrefab, initialName);
                const next = initialName ? await resolveClip(initialPrefab, initialName) : null;
                if (cancelled) return;
                if (next) loadedKey.current = serializedClip(next);
                setPrefabId(initialPrefab.id);
                setClipName(initialName);
                setClip(next);
                setSelection({ actorId: next?.actors[0]?.id || null });
                stage.show(next, 0);
                setReady(true);
            } catch (error) {
                setStatus('Could not show the pack opener.');
            }
        };

        boot();

        return () => {
            cancelled = true;
            loadToken.current += 1;
            playbackRef.current?.kill();
            playingRef.current = false;
            if (stageRef.current && app) {
                stageRef.current.detach(app);
                stageRef.current = null;
            }
            setAutoResize(false);
            setReady(false);
        };
    }, [app, setAutoResize]);

    useEffect(() => {
        if (!app) return undefined;

        const onResize = () => {
            stageRef.current?.resize(app.screen.width, app.screen.height);
            if (!playingRef.current) stageRef.current?.show(clipRef.current, timeRef.current);
        };

        window.addEventListener('appResize', onResize);
        return () => window.removeEventListener('appResize', onResize);
    }, [app]);

    useEffect(() => {
        if (!ready || !clip) return undefined;
        if (playingRef.current) halt();
        stageRef.current?.show(clip, timeRef.current);
        return undefined;
    }, [ready, clip]);

    useEffect(() => {
        if (!ready || !clip || playingRef.current) return undefined;
        if (releasePoseRef.current) {
            stageRef.current?.resetTransforms();
            return undefined;
        }
        stageRef.current?.show(clipRef.current, time);
        return undefined;
    }, [ready, clip, time]);

    useEffect(() => {
        if (!clip || !clipName || !prefabId) return undefined;
        const serialized = serializedClip(clip);
        if (serialized === loadedKey.current) return undefined;

        const timer = setTimeout(() => {
            try {
                saveDraft(prefabId, clipName, clip);
                loadedKey.current = serializedClip(clip);
                setLibraryRevision((value) => value + 1);
            } catch (error) {
                setStatus('Draft is too large for browser storage. Export the JSON to keep it.');
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [clip, clipName, prefabId]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const tag = event.target.tagName;
            if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
            if (event.code === 'Space') {
                event.preventDefault();
                if (playingRef.current) pauseRef.current();
                else playRef.current();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const onImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !clipName) return;
        try {
            const next = normalizeClip(JSON.parse(await file.text()));
            loadedKey.current = '';
            halt();
            setClip(next);
            setPlayhead(0);
            setSelection({ actorId: next.actors[0]?.id || null });
            setStatus(`Imported ${file.name} into ${clipName}.`);
        } catch (error) {
            setStatus('That file is not an animation clip.');
        }
    };

    const createClip = () => {
        if (!prefab) return;
        const name = draftName.trim();
        if (!CLIP_NAME.test(name)) {
            setStatus('Use a name like flourish or open-pack.');
            return;
        }
        if (clipEntries.some((entry) => entry.name === name)) {
            setStatus(`${name} already exists on this prefab.`);
            return;
        }
        try {
            flushDraft();
        } catch (error) {
            setStatus('Draft is too large for browser storage. Export the JSON to keep it.');
            return;
        }

        const next = createEmptyClip(name);
        try {
            saveDraft(prefab.id, name, next);
        } catch (error) {
            setStatus('Could not store that clip in the browser.');
            return;
        }
        loadedKey.current = serializedClip(next);
        halt();
        setClipName(name);
        setClip(next);
        setDraftName('');
        setPlayhead(0);
        setSelection(null);
        setLibraryRevision((value) => value + 1);
        setStatus(`Created ${name}. It stays in this browser until you export it and stub it on the prefab.`);
    };

    const duplicateClip = () => {
        if (!prefab || !clip || !clipName) return;
        const taken = new Set(clipEntries.map((entry) => entry.name));
        let name = `${clipName}-copy`;
        let guard = 0;
        while (taken.has(name) && guard < 20) {
            guard += 1;
            name = `${clipName}-copy-${guard + 1}`;
        }
        try {
            flushDraft();
            const next = normalizeClip({ ...clip, name });
            saveDraft(prefab.id, name, next);
            loadedKey.current = serializedClip(next);
            halt();
            setClipName(name);
            setClip(next);
            setPlayhead(0);
            setLibraryRevision((value) => value + 1);
            setStatus(`Duplicated as ${name}.`);
        } catch (error) {
            setStatus('Could not store that clip in the browser.');
        }
    };

    const deleteClip = () => {
        if (!prefab || !currentEntry || currentEntry.stubbed) return;
        if (clipRef.current) loadedKey.current = serializedClip(clipRef.current);
        deleteDraft(prefab.id, clipName);
        const rest = listClipEntries(prefab);
        setLibraryRevision((value) => value + 1);
        if (rest[0]) {
            openClip(prefab, rest[0].name);
            return;
        }
        halt();
        setClip(null);
        setClipName(null);
        setSelection(null);
        setStatus(`Removed ${clipName}.`);
    };

    const clearAllDrafts = async () => {
        clearDrafts();
        setLibraryRevision((value) => value + 1);
        if (!prefab) {
            setStatus('Cleared every local draft.');
            return;
        }

        const rest = listClipEntries(prefab);
        const nextName = rest.some((entry) => entry.name === clipName) ? clipName : rest[0]?.name;
        if (!nextName) {
            halt();
            setClip(null);
            setClipName(null);
            setSelection(null);
            setStatus('Cleared every local draft.');
            return;
        }

        const next = await resolveClip(prefab, nextName);
        loadedKey.current = serializedClip(next);
        halt();
        setClipName(nextName);
        setClip(next);
        setPlayhead(0);
        setSelection({ actorId: next.actors[0]?.id || null });
        setStatus('Cleared every local draft.');
    };

    const revertClip = async () => {
        if (!prefab || !currentEntry?.stubbed || !currentEntry.draft) return;
        if (clipRef.current) loadedKey.current = serializedClip(clipRef.current);
        deleteDraft(prefab.id, clipName);
        const next = await resolveClip(prefab, clipName);
        loadedKey.current = serializedClip(next);
        halt();
        setClip(next);
        setPlayhead(0);
        setSelection({ actorId: next.actors[0]?.id || null });
        setLibraryRevision((value) => value + 1);
        setStatus(`${clipName} is back to the stubbed file.`);
    };

    const scrubFrom = (event) => {
        if (event.target.closest('.anim-editor__key')) return;
        halt();
        const apply = (moveEvent) => setPlayhead(timeFromClientX(moveEvent.clientX));
        apply(event);
        const onMove = (moveEvent) => apply(moveEvent);
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const dragKey = (event, trackId, keyId) => {
        event.stopPropagation();
        event.preventDefault();
        halt();
        setSelection({ trackId, keyId });
        const onMove = (moveEvent) => {
            const nextTime = timeFromClientX(moveEvent.clientX);
            setClip((current) => updateKey(current, trackId, keyId, { time: nextTime }));
            setPlayhead(nextTime);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const selectedTrack = clip?.tracks.find((track) => track.id === selection?.trackId) || null;
    const selectedKey = selectedTrack?.keys.find((key) => key.id === selection?.keyId) || null;
    const selectedActorId = clip?.actors.some((actor) => actor.id === selection?.actorId)
        ? selection.actorId
        : (selectedTrack?.actorId || clip?.actors[0]?.id);
    const selectedActor = clip?.actors.find((actor) => actor.id === selectedActorId) || null;
    const actorProperties = propertiesForActor(selectedActor);
    const openProperties = actorProperties.filter((property) => (
        !clip?.tracks.some((track) => track.actorId === selectedActorId && track.property === property.id)
    ));
    const propertyLabel = (actorId, propertyId) => {
        const actor = clip?.actors.find((item) => item.id === actorId);
        return resolveProperty(actorId, propertyId, actor?.properties)?.label || propertyId;
    };
    const propertyFor = (actorId, propertyId) => {
        const actor = clip?.actors.find((item) => item.id === actorId);
        return resolveProperty(actorId, propertyId, actor?.properties);
    };
    const propertyStep = (actorId, propertyId) => propertyFor(actorId, propertyId)?.step || 0.01;
    const propertyIsBoolean = (actorId, propertyId) => propertyFor(actorId, propertyId)?.type === 'boolean';
    const ticks = rulerTicks(clip?.length || 1);
    const parts = listParts(prefab);

    const deleteSelection = useCallback(() => {
        if (!selection?.keyId) return;
        setClip((current) => removeKey(current, selection.trackId, selection.keyId));
        setSelection({ trackId: selection.trackId });
    }, [selection]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const tag = event.target.tagName;
            if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
            if (event.key === 'Delete' || event.key === 'Backspace') deleteSelection();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [deleteSelection]);

    return (
        <div className="anim-editor">
            <header className="anim-editor__toolbar">
                <label className="anim-editor__field">
                    Clip
                    <input
                        className="anim-editor__name"
                        value={clip?.name || ''}
                        disabled={!clip}
                        onChange={(event) => setClip((current) => (
                            current ? { ...current, name: event.target.value } : current
                        ))}
                    />
                </label>
                <label className="anim-editor__field">
                    Length
                    <input
                        className="anim-editor__number"
                        type="number"
                        min="0.05"
                        step="0.05"
                        value={clip?.length ?? 1}
                        disabled={!clip}
                        onChange={(event) => setClip((current) => setClipLength(current, Number(event.target.value)))}
                    />
                </label>
                <label className="anim-editor__field">
                    FPS
                    <input
                        className="anim-editor__number"
                        type="number"
                        min="1"
                        max="120"
                        step="1"
                        value={clip?.frameRate ?? 60}
                        disabled={!clip}
                        onChange={(event) => setClip((current) => setClipFrameRate(current, Number(event.target.value)))}
                    />
                </label>
                <label className="anim-editor__field anim-editor__snap">
                    <span>Loop</span>
                    <input
                        type="checkbox"
                        checked={Boolean(clip?.loop)}
                        disabled={!clip}
                        onChange={(event) => setClip((current) => (
                            current ? { ...current, loop: event.target.checked } : current
                        ))}
                    />
                </label>
                <label className="anim-editor__field anim-editor__snap">
                    <span>Keep final values</span>
                    <input
                        type="checkbox"
                        checked={clip?.keepFinal === true}
                        disabled={!clip}
                        onChange={(event) => setClip((current) => (
                            current ? { ...current, keepFinal: event.target.checked } : current
                        ))}
                    />
                </label>
                <div className="anim-editor__actions">
                    <label className="anim-editor__file">
                        Import
                        <input type="file" accept="application/json" onChange={onImport} disabled={!clip} />
                    </label>
                    <button
                        type="button"
                        disabled={!clip}
                        onClick={async () => {
                            try {
                                const fileName = await saveClip(clip, `${clipName}.json`);
                                setStatus(`Exported ${fileName}.`);
                            } catch (error) {
                                if (error?.name === 'AbortError') return;
                                setStatus('Could not export the clip.');
                            }
                        }}
                    >
                        Export JSON
                    </button>
                    <button
                        type="button"
                        disabled={!clip}
                        onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(JSON.stringify(normalizeClip(clip), null, 2));
                                setStatus('Copied the clip JSON.');
                            } catch (error) {
                                setStatus('Could not copy the clip JSON.');
                            }
                        }}
                    >
                        Copy JSON
                    </button>
                </div>
                <p className="anim-editor__status">{status}</p>
            </header>

            <aside className="anim-editor__actors">
                <h1>Prefab</h1>
                <label className="anim-editor__field">
                    Container
                    <select
                        className="anim-editor__prefab"
                        value={prefabId}
                        onChange={(event) => {
                            const nextPrefab = getPrefab(event.target.value);
                            const entries = listClipEntries(nextPrefab);
                            if (entries[0]) {
                                openClip(nextPrefab, entries[0].name);
                                return;
                            }
                            try {
                                flushDraft();
                            } catch (error) {
                                setStatus('Draft is too large for browser storage. Export the JSON to keep it.');
                            }
                            stageRef.current?.mountPrefab(nextPrefab).catch(() => {
                                setStatus('Could not show that prefab.');
                            });
                            halt();
                            setPrefabId(nextPrefab.id);
                            setClipName(null);
                            setClip(null);
                            setSelection(null);
                        }}
                    >
                        {listPrefabs().map((entry) => (
                            <option key={entry.id} value={entry.id}>{entry.name}</option>
                        ))}
                    </select>
                </label>

                <h1 className="anim-editor__section">Clips</h1>
                <p className="anim-editor__hint">
                    Stubbed clips are the names code can play. Local clips stay in this browser until you export them and add a stub.
                </p>
                <div className="anim-editor__new-clip">
                    <input
                        placeholder="clip name"
                        value={draftName}
                        onChange={(event) => setDraftName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') createClip();
                        }}
                    />
                    <button type="button" onClick={createClip}>New</button>
                </div>
                <ul className="anim-editor__catalog">
                    {clipEntries.map((entry) => (
                        <li
                            key={entry.name}
                            className={entry.name === clipName ? 'anim-editor__catalog-item--selected' : ''}
                        >
                            <button
                                type="button"
                                className="anim-editor__track-pick"
                                onClick={() => openClip(prefab, entry.name)}
                            >
                                <b>{entry.name}</b>
                            </button>
                            <span>{entry.stubbed ? 'Stubbed' : 'Local'}</span>
                        </li>
                    ))}
                </ul>
                <div className="anim-editor__actions">
                    <button type="button" disabled={!clip} onClick={duplicateClip}>Duplicate</button>
                    <button type="button" disabled={!currentEntry || currentEntry.stubbed} onClick={deleteClip}>
                        Delete
                    </button>
                    <button
                        type="button"
                        disabled={!currentEntry?.stubbed || !currentEntry.draft}
                        onClick={revertClip}
                    >
                        Revert
                    </button>
                    <button
                        type="button"
                        disabled={!listPrefabs().some((item) => listClipEntries(item).some((entry) => entry.draft))}
                        onClick={clearAllDrafts}
                    >
                        Clear drafts
                    </button>
                </div>

                <h1 className="anim-editor__section">Parts</h1>
                <p className="anim-editor__hint">
                    {clip
                        ? 'Add a part to this clip, then animate its position, rotation, scale, or alpha.'
                        : 'Create a clip, then add a part to it.'}
                </p>
                <ul className="anim-editor__catalog">
                    {parts.map((entry) => {
                        const inClip = clip?.actors.some((actor) => actor.id === entry.id);
                        return (
                            <li key={entry.id}>
                                <span className="anim-editor__catalog-name">
                                    <b>{entry.name}</b>
                                    <span>{entry.id}</span>
                                </span>
                                <button
                                    type="button"
                                    disabled={!clip || inClip}
                                    onClick={() => {
                                        const added = addAnimatable(clip, entry);
                                        setClip(added.clip);
                                        setSelection({ actorId: added.actorId });
                                    }}
                                >
                                    {inClip ? 'In clip' : 'Add'}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {clip?.actors.length > 0 && <h1 className="anim-editor__section">On this clip</h1>}
                {clip?.actors.map((actor) => {
                    const selected = actor.id === selectedActorId;
                    return (
                        <div
                            key={actor.id}
                            className={`anim-editor__actor${selected ? ' anim-editor__actor--selected' : ''}`}
                            onFocus={() => setSelection({ actorId: actor.id })}
                        >
                            <div className="anim-editor__actor-head">
                                <input
                                    value={actor.name}
                                    onChange={(event) => setClip((current) => updateActor(current, actor.id, { name: event.target.value }))}
                                />
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setClip((current) => removeActor(current, actor.id));
                                        setSelection(null);
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                            <button type="button" className="anim-editor__id" onClick={() => setSelection({ actorId: actor.id })}>
                                {actor.id}
                            </button>
                            {selected && (
                                <div className="anim-editor__custom">
                                    <span>Initial</span>
                                    <p className="anim-editor__hint">
                                        Applied when this clip starts, before the first key. Position and rotation keys are added to it. Alpha 0 hides the part so it does not flash.
                                    </p>
                                    {Object.entries(actor.initial || {}).map(([propertyId, value]) => (
                                        <div key={propertyId} className="anim-editor__marked">
                                            <span>{propertyLabel(actor.id, propertyId)} · {String(value)}</span>
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setClip((current) => clearActorInitial(current, actor.id, propertyId));
                                                }}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    ))}
                                    <div className="anim-editor__custom-row">
                                        <select
                                            value={initialDraft.property}
                                            onChange={(event) => {
                                                const property = propertiesForActor(actor).find((item) => item.id === event.target.value);
                                                setInitialDraft({
                                                    property: event.target.value,
                                                    value: property?.type === 'boolean' ? property.defaultValue === true : (property?.defaultValue ?? 0),
                                                });
                                            }}
                                        >
                                            {propertiesForActor(actor).map((property) => (
                                                <option key={property.id} value={property.id}>{property.label}</option>
                                            ))}
                                        </select>
                                        {propertyIsBoolean(actor.id, initialDraft.property) ? (
                                            <select
                                                value={initialDraft.value === true ? 'true' : 'false'}
                                                onChange={(event) => setInitialDraft((draft) => ({
                                                    ...draft,
                                                    value: event.target.value === 'true',
                                                }))}
                                            >
                                                <option value="true">true</option>
                                                <option value="false">false</option>
                                            </select>
                                        ) : (
                                            <input
                                                type="number"
                                                step={propertyStep(actor.id, initialDraft.property)}
                                                value={initialDraft.value}
                                                onChange={(event) => setInitialDraft((draft) => ({
                                                    ...draft,
                                                    value: Number(event.target.value),
                                                }))}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setClip((current) => setActorInitial(
                                                    current,
                                                    actor.id,
                                                    initialDraft.property,
                                                    initialDraft.value,
                                                ));
                                            }}
                                        >
                                            Set
                                        </button>
                                    </div>
                                </div>
                            )}
                            {selected && (
                                <div className="anim-editor__custom">
                                    <span>Custom property</span>
                                    <input
                                        placeholder="id"
                                        value={customDraft.id}
                                        onChange={(event) => setCustomDraft((draft) => ({ ...draft, id: event.target.value }))}
                                    />
                                    <input
                                        placeholder="Label"
                                        value={customDraft.label}
                                        onChange={(event) => setCustomDraft((draft) => ({ ...draft, label: event.target.value }))}
                                    />
                                    <div className="anim-editor__custom-row">
                                        <label className="anim-editor__field">
                                            Default
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={customDraft.defaultValue}
                                                onChange={(event) => setCustomDraft((draft) => ({
                                                    ...draft,
                                                    defaultValue: Number(event.target.value),
                                                }))}
                                            />
                                        </label>
                                        <label className="anim-editor__field">
                                            Step
                                            <input
                                                type="number"
                                                min="0.001"
                                                step="0.01"
                                                value={customDraft.step}
                                                onChange={(event) => setCustomDraft((draft) => ({
                                                    ...draft,
                                                    step: Number(event.target.value),
                                                }))}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                const next = addCustomProperty(clip, actor.id, customDraft);
                                                if (next === clip) {
                                                    setStatus('Use a plain name that is not already a built-in channel.');
                                                    return;
                                                }
                                                setClip(next);
                                                setCustomDraft({ id: '', label: '', defaultValue: 0, step: 0.01 });
                                                setStatus(`Marked ${customDraft.id.trim()} on ${actor.name}. The curve offsets the value at playback start.`);
                                            }}
                                        >
                                            Mark
                                        </button>
                                    </div>
                                    {(actor.properties || []).map((property) => (
                                        <div key={property.id} className="anim-editor__marked">
                                            <span>{property.label} · {property.id}</span>
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setClip((current) => removeCustomProperty(current, actor.id, property.id));
                                                }}
                                            >
                                                Unmark
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selected && (
                                <div className="anim-editor__add-track">
                                    <select
                                        value={openProperties.some((property) => property.id === trackProperty)
                                            ? trackProperty
                                            : (openProperties[0]?.id || '')}
                                        onChange={(event) => setTrackProperty(event.target.value)}
                                    >
                                        {openProperties.map((property) => (
                                            <option key={property.id} value={property.id}>{property.label}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        disabled={openProperties.length === 0}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const propertyId = openProperties.some((property) => property.id === trackProperty)
                                                ? trackProperty
                                                : openProperties[0]?.id;
                                            const added = addTrack(clip, actor.id, propertyId);
                                            setClip(added.clip);
                                            if (added.trackId) setSelection({ trackId: added.trackId });
                                        }}
                                    >
                                        Add track
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </aside>

            <div className="anim-editor__preview" ref={pixiRef} />

            <aside className="anim-editor__inspector">
                <h2>Key</h2>
                {selectedKey && selectedTrack ? (
                    <>
                        <b>{selectedActor?.name} · {propertyLabel(selectedTrack.actorId, selectedTrack.property)}</b>
                        <label className="anim-editor__field">
                            Time
                            <input
                                className="anim-editor__number"
                                type="number"
                                min="0"
                                max={clip.length}
                                step={snap ? 1 / clip.frameRate : 0.01}
                                value={selectedKey.time}
                                onChange={(event) => {
                                    const next = Number(event.target.value);
                                    if (!Number.isFinite(next)) return;
                                    const nextTime = snapTime(next, clip.frameRate, clip.length, snap);
                                    setClip((current) => updateKey(current, selectedTrack.id, selectedKey.id, { time: nextTime }));
                                    setPlayhead(nextTime);
                                }}
                            />
                        </label>
                        <label className="anim-editor__field">
                            Value
                            {propertyIsBoolean(selectedTrack.actorId, selectedTrack.property) ? (
                                <select
                                    value={selectedKey.value === true ? 'true' : 'false'}
                                    onChange={(event) => setClip((current) => updateKey(
                                        current,
                                        selectedTrack.id,
                                        selectedKey.id,
                                        { value: event.target.value === 'true' },
                                    ))}
                                >
                                    <option value="true">true</option>
                                    <option value="false">false</option>
                                </select>
                            ) : (
                                <input
                                    className="anim-editor__number"
                                    type="number"
                                    step={propertyStep(selectedTrack.actorId, selectedTrack.property)}
                                    value={selectedKey.value}
                                    onChange={(event) => {
                                        const next = Number(event.target.value);
                                        if (!Number.isFinite(next)) return;
                                        setClip((current) => updateKey(current, selectedTrack.id, selectedKey.id, { value: next }));
                                    }}
                                />
                            )}
                        </label>
                        {propertyIsBoolean(selectedTrack.actorId, selectedTrack.property) ? null : (
                            <>
                                <label className="anim-editor__field">
                                    Ease to next
                                    <select
                                        value={isCubicEase(selectedKey.ease) ? CUSTOM_EASE : selectedKey.ease}
                                        onChange={(event) => {
                                            const next = event.target.value;
                                            const ease = next === CUSTOM_EASE
                                                ? formatCubicEase(...DEFAULT_CURVE)
                                                : next;
                                            setClip((current) => updateKey(
                                                current,
                                                selectedTrack.id,
                                                selectedKey.id,
                                                { ease },
                                            ));
                                        }}
                                    >
                                        {easeOptions(selectedKey.ease).map((ease) => (
                                            <option key={ease} value={ease}>{ease === CUSTOM_EASE ? 'Custom' : ease}</option>
                                        ))}
                                    </select>
                                </label>
                                {isCubicEase(selectedKey.ease) && (
                                    <CurveEditor
                                        ease={selectedKey.ease}
                                        onChange={(ease) => setClip((current) => updateKey(
                                            current,
                                            selectedTrack.id,
                                            selectedKey.id,
                                            { ease },
                                        ))}
                                    />
                                )}
                            </>
                        )}
                        <button type="button" onClick={deleteSelection}>Delete key</button>
                    </>
                ) : (
                    <p className="anim-editor__hint">Select a key on the timeline to edit its time, value, and ease.</p>
                )}
            </aside>

            <section className="anim-editor__timeline">
                <div className="anim-editor__transport">
                    <button type="button" disabled={!clip} onClick={playing ? pause : play}>{playing ? 'Pause' : 'Play'}</button>
                    <button type="button" disabled={!clip} onClick={stop}>Stop</button>
                    <span className="anim-editor__clock">
                        {formatTime(time)} / {formatTime(clip?.length || 0)}
                    </span>
                    <label className="anim-editor__field anim-editor__snap">
                        <span>Snap</span>
                        <input type="checkbox" checked={snap} onChange={(event) => setSnap(event.target.checked)} />
                    </label>
                </div>

                {clip ? (
                    <div className="anim-editor__sheet">
                        <div className="anim-editor__labels">
                            <div className="anim-editor__label">Track</div>
                            {clip.tracks.map((track) => {
                                const actor = clip.actors.find((item) => item.id === track.actorId);
                                const selected = track.id === selectedTrack?.id;
                                return (
                                    <div
                                        key={track.id}
                                        className={`anim-editor__label${selected ? ' anim-editor__label--selected' : ''}`}
                                    >
                                        <button
                                            type="button"
                                            className="anim-editor__track-pick"
                                            onClick={() => setSelection({ trackId: track.id })}
                                        >
                                            {actor?.name || track.actorId} · {propertyLabel(track.actorId, track.property)}
                                        </button>
                                        <button type="button" onClick={(event) => {
                                            event.stopPropagation();
                                            setClip((current) => removeTrack(current, track.id));
                                            setSelection({ actorId: track.actorId });
                                        }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="anim-editor__lanes" ref={lanesRef} onPointerDown={scrubFrom}>
                            <div className="anim-editor__ruler">
                                {ticks.map((tick) => (
                                    <span key={tick} className="anim-editor__tick" style={{ left: `${(tick / clip.length) * 100}%` }}>
                                        {tick.toFixed(1)}
                                    </span>
                                ))}
                            </div>
                            {clip.tracks.length === 0 && <div className="anim-editor__empty">Add a part, then add a track.</div>}
                            {clip.tracks.map((track) => {
                                const selected = track.id === selectedTrack?.id;
                                return (
                                    <div
                                        key={track.id}
                                        className={`anim-editor__lane${selected ? ' anim-editor__lane--selected' : ''}`}
                                        onDoubleClick={(event) => {
                                            const nextTime = timeFromClientX(event.clientX);
                                            const actor = clip.actors.find((item) => item.id === track.actorId);
                                            const property = resolveProperty(track.actorId, track.property, actor?.properties);
                                            setClip((current) => addKey(current, track.id, nextTime, heldValue(track, nextTime, property)));
                                            setPlayhead(nextTime);
                                            setSelection({ trackId: track.id });
                                        }}
                                    >
                                        {track.keys.map((key) => (
                                            <div
                                                key={key.id}
                                                className={`anim-editor__key${key.id === selectedKey?.id ? ' anim-editor__key--selected' : ''}`}
                                                style={{ left: `${(key.time / clip.length) * 100}%` }}
                                                onPointerDown={(event) => dragKey(event, track.id, key.id)}
                                            />
                                        ))}
                                    </div>
                                );
                            })}
                            <div className="anim-editor__playhead" style={{ left: `${(time / clip.length) * 100}%` }} />
                        </div>
                    </div>
                ) : (
                    <p className="anim-editor__hint">This prefab has no clips yet.</p>
                )}
            </section>
        </div>
    );
}
